import { useRouter } from 'next/router';
import React, { memo, useContext } from 'react';
import type { FC, PropsWithChildren } from 'react';

import { useIsMine, usePermissions } from '../../hooks/usePermissions';
import type { Message, User } from '../../types/api';

const MessageEditingContext = React.createContext<boolean>(false);

interface MessageEditingProviderProps {
  message?: Message;
}

export const MessageEditingProvider: FC<PropsWithChildren<MessageEditingProviderProps>> = memo(
  function MessageEditingProvider(props) {
    const { children, message } = props;

    const { query } = useRouter();
    const targetAuthor = message?.author as User;
    const targetIsMine = useIsMine(targetAuthor);

    const isEditing = targetIsMine && query.edit === 'true';

    return (
      <MessageEditingContext.Provider value={isEditing}>{children}</MessageEditingContext.Provider>
    );
  },
);

/** To get a real value (not the default false), you need to run it inside a MessageEditingProvider. */
export function useMessageEditing() {
  return useContext(MessageEditingContext);
}

export function useIsEditingPermissionAvailable() {
  const { query } = useRouter();
  const { isAuthDone } = usePermissions();
  return query.edit !== 'true' || isAuthDone;
}
